import { type ReactNode, Suspense, useState } from 'react';
import { Provider } from 'react-redux';

import PageLoader from 'components/page-loader/page-loader';
import { useSuspenseQuery } from '@tanstack/react-query';
import { rootStore } from './Store';
import type { ContentType } from 'utils/getContentType';
import { setContentId, setContentType, setDisplayDocumentTotal, setIsLocked, setTotalType } from './documentSettingsSlice';
import { contentQueries } from 'services/content/contentQueries';

export function assertPreloaded<T>(value: T | null | undefined, name: string): T {
  if (value === null || value === undefined) {
    throw new Error(`StorePreloader: '${name}' read before preload completed`);
  }
  return value;
}

interface Props {
  contentId: string;
  contentType: ContentType;
  children: ReactNode;
}

function Inner({ children, contentId, contentType }: Props) {
  const { data } = useSuspenseQuery(contentQueries.contentMetadataById(contentType, contentId));

  useState(() => {
    rootStore.dispatch(setContentId(contentId));
    rootStore.dispatch(setContentType(contentType));
    rootStore.dispatch(setIsLocked(data.isLocked));
    rootStore.dispatch(setTotalType(data.totalType));
    rootStore.dispatch(setDisplayDocumentTotal(data.displayDocumentTotal ?? true));
    return true;
  });

  return <Provider store={rootStore}>{children}</Provider>;
}

export function StorePreloader({ contentId, contentType, children }: Props) {
  return (
    <Suspense fallback={<PageLoader isLoading isLoaded={false} />}>
      <Inner contentId={contentId} contentType={contentType}>
        {children}
      </Inner>
    </Suspense>
  );
}
